<script setup>
/**
 * Lucky Draw - Multiple Entries. Mirrors Vue2's PreDraw2.vue: a fully
 * client-side random-draw tool (no backend calls) — upload participant +
 * prize lists, randomly assign prizes in-browser, export winners as .xlsx.
 */
import { ref } from "vue";

const participantsFileName = ref("");
const prizesFileName = ref("");
const idList = ref([]);
const rawPrizeList = ref([]);
const startingSN = ref(1);
const winnersList = ref([]);
const error = ref("");

async function parseFile(file) {
  const buffer = await file.arrayBuffer();
  try {
    const { read, utils } = await import("xlsx");
    const wb = read(buffer);
    return utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
  } catch {
    const text = new TextDecoder().decode(buffer);
    const lines = text.trim().split(/\r?\n/);
    const headers = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const values = line.split(",");
      return Object.fromEntries(headers.map((h, i) => [h, (values[i] ?? "").trim()]));
    });
  }
}

async function handleParticipantsFile(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file) return;
  participantsFileName.value = file.name;
  idList.value = await parseFile(file);
}

async function handlePrizesFile(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file) return;
  prizesFileName.value = file.name;
  rawPrizeList.value = await parseFile(file);
}

function pickWinners() {
  error.value = "";
  if (!participantsFileName.value) {
    error.value = "Please include the participant list.";
    return;
  }
  if (!prizesFileName.value) {
    error.value = "Please include the prize list.";
    return;
  }
  winnersList.value = [];
  const remaining = [...idList.value];
  let sn = startingSN.value;
  const filteredPrizes = rawPrizeList.value.filter(
    (row) => row.Prize !== undefined && row.Prize !== null && row.Prize !== "",
  );
  for (const prize of filteredPrizes) {
    for (let j = 0; j < prize.Qty; j++) {
      if (remaining.length === 0) break;
      const randomIndex = Math.floor(Math.random() * remaining.length);
      const winner = remaining.splice(randomIndex, 1)[0];
      winnersList.value.push({
        sn: sn++,
        name: winner.Name,
        id: winner["Employee ID"],
        prize: prize.Prize,
      });
    }
  }
}

async function exportWinners() {
  const { utils, write } = await import("xlsx");
  const rows = winnersList.value.map((w) => ({
    "S/N": w.sn,
    Name: w.name,
    "Staff ID": w.id,
    Prize: w.prize,
  }));
  const ws = utils.json_to_sheet(rows);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, "Winners");
  const buffer = write(wb, { type: "array", bookType: "xlsx" });
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "Winners.xlsx";
  link.click();
  window.URL.revokeObjectURL(url);
}
</script>

<template>
  <section>
    <h1 class="text-h5 mb-4">Lucky Draw - Multiple Entries</h1>

    <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>

    <VCard class="pa-4 mb-4" max-width="560">
      <h3 class="mb-2">Step 1: Upload the list of Participants</h3>
      <p class="text-caption text-medium-emphasis mb-2">Headers: Name, Employee ID</p>
      <input type="file" accept=".csv,.xlsx" @change="handleParticipantsFile" />
      <p v-if="participantsFileName" class="text-caption text-medium-emphasis mt-1">
        {{ participantsFileName }} — {{ idList.length }} participant(s)
      </p>

      <h3 class="mb-2 mt-6">Step 2: Upload the list of Prizes</h3>
      <p class="text-caption text-medium-emphasis mb-2">Headers: Prize, Qty</p>
      <input type="file" accept=".csv,.xlsx" @change="handlePrizesFile" />
      <p v-if="prizesFileName" class="text-caption text-medium-emphasis mt-1">
        {{ prizesFileName }} — {{ rawPrizeList.length }} prize row(s)
      </p>

      <VBtn color="primary" class="mt-4" @click="pickWinners">Submit</VBtn>
    </VCard>

    <VCard v-if="winnersList.length > 0" class="pa-4">
      <h3 class="mb-2">Winners</h3>
      <VTable density="compact" class="mb-4">
        <thead>
          <tr>
            <th>S/N</th>
            <th>Name</th>
            <th>Staff ID</th>
            <th>Prize</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(winner, i) in winnersList" :key="i">
            <td>{{ winner.sn }}</td>
            <td>{{ winner.name }}</td>
            <td>{{ winner.id }}</td>
            <td>{{ winner.prize }}</td>
          </tr>
        </tbody>
      </VTable>
      <VBtn color="primary" @click="exportWinners">Export Winners</VBtn>
    </VCard>
  </section>
</template>
